import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useFileThumbnail } from "@/features/dossiers/hooks/use-dossier-files";
import type { DossierFile } from "@/features/dossiers/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { formatFileSize } from "@/features/utils/numbers";
import {
  DownloadSimpleIcon,
  PencilSimpleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { fileIcon } from "./file-icon";

/** One file: its name opens it, the meta line says what, when and who. */
export function FileRow({
  file,
  onOpen,
  onDownload,
  onDelete,
  onRename,
  compact = false,
}: {
  file: DossierFile;
  onOpen: () => void;
  onDownload: () => void;
  /** Absent: no delete button (no permission, or inside a popover). */
  onDelete?: () => void;
  /** Absent: no rename button. Resolves `false` when the name was refused. */
  onRename?: (filename: string) => Promise<boolean>;
  compact?: boolean;
}) {
  const { t } = useTranslation("dossiers");
  const { format } = useDateFormat();
  const Icon = fileIcon(file);
  const thumbnail = useFileThumbnail(file);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(file.filename);

  const startEditing = () => {
    setName(file.filename);
    setEditing(true);
  };
  const commit = async () => {
    if (!onRename) return setEditing(false);
    // Stay in the field when the server refused the name: it is still there to fix.
    if (await onRename(name)) setEditing(false);
  };

  const meta = [
    formatFileSize(file.size),
    file.created_at ? format(file.created_at, compact ? "P" : "Pp") : "",
    !compact && file.uploaded_by_name
      ? t("row.by", { name: file.uploaded_by_name })
      : "",
    !compact && file.origin !== "contact" ? t(`origin.${file.origin}`) : "",
  ].filter(Boolean);

  return (
    <li className="group flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/60">
      <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-primary/10">
        {thumbnail && !failed ? (
          <img
            src={thumbnail}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            className="size-full object-cover"
          />
        ) : (
          <Icon className="size-4 text-primary" weight="duotone" aria-hidden />
        )}
      </span>
      {editing ? (
        <Input
          autoFocus
          value={name}
          maxLength={200}
          aria-label={t("rename.label")}
          onChange={(event) => setName(event.target.value)}
          onFocus={(event) => {
            // Select the name, not its extension: that is what gets retyped.
            const dot = event.target.value.lastIndexOf(".");
            event.target.setSelectionRange(0, dot > 0 ? dot : name.length);
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
            if (event.key === "Escape") {
              // Unmount first, or the blur would save what Escape cancels.
              setEditing(false);
            }
          }}
          className="h-8 min-w-0 flex-1"
        />
      ) : (
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left"
          title={file.filename}
        >
          <span className="block truncate text-sm font-medium hover:underline">
            {file.filename}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {meta.join(" · ")}
          </span>
        </button>
      )}
      <div className="flex shrink-0 items-center opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
        <Button
          type="button"
          variant="ghost"
          size="xsicon"
          tooltip={t("actions.download")}
          aria-label={t("actions.download")}
          onClick={onDownload}
        >
          <DownloadSimpleIcon className="size-3.5" />
        </Button>
        {onRename && (
          <Button
            type="button"
            variant="ghost"
            size="xsicon"
            tooltip={t("actions.rename")}
            aria-label={t("actions.rename")}
            onClick={startEditing}
          >
            <PencilSimpleIcon className="size-3.5" />
          </Button>
        )}
        {onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="xsicon"
            tooltip={t("actions.delete")}
            aria-label={t("actions.delete")}
            confirm={t("actions.confirmDelete", { name: file.filename })}
            onClick={onDelete}
          >
            <TrashIcon className="size-3.5" />
          </Button>
        )}
      </div>
    </li>
  );
}
